import { html } from 'htm/preact';
import { useEffect, useState } from 'react';
import { ConcelhoRecord, NationalSummary } from '@utils/eclipse2026';
import { coarseCountdown, downloadIcs, eventForConcelho, eventForCountry } from '../lib/calendar';

/**
 * Bloco do calendário: botão .ics, a contagem ao vivo, e a nota do próximo eclipse.
 *
 * A contagem actualiza **ao minuto**, não ao segundo: a granularidade mostrada é
 * "7d 06h 12m" / "4h 12m", pelo que ticar mais depressa seria trabalho invisível. Ao
 * contrário do banner da homepage, aqui não há um `serverNow` para servir de guarda
 * de desvio — o relógio é o do dispositivo do leitor, e se estiver errado a contagem
 * estará errada. Numa peça onde a informação a sério são as horas locais (que vêm
 * calculadas do gerador, não do relógio), é uma limitação aceitável.
 */

const MINUTE = 60_000;

/** As datas do próximo eclipse visível de Portugal são factos fixos, não dados
 *  gerados — não faz sentido pô-los no JSON de um evento que já passou. */
const NEXT_ECLIPSES = 'Se perder este: o próximo eclipse visível de Portugal é anular, a 26 de janeiro de 2028. Antes disso, a 2 de agosto de 2027, há um eclipse total no sul de Espanha.';

export function CalendarBlock({ concelho, national, dateLabel, articleUrl }: { concelho: ConcelhoRecord | null; national: NationalSummary; dateLabel: string; articleUrl: string }) {
    const [nowMs, setNowMs] = useState(() => Date.now());

    useEffect(() => {
        const id = window.setInterval(() => setNowMs(Date.now()), MINUTE);
        return () => window.clearInterval(id);
    }, []);

    const beginMs = Date.parse(concelho ? concelho.partialBegin.utc : national.partialBegin.firstUtc);
    const endMs = Date.parse(concelho ? concelho.displayEnd.utc : national.partialEnd.lastUtc);

    // Sem concelho escolhido, a contagem é para o primeiro início do país e o rótulo
    // di-lo — prometer "na sua terra" sem saber qual é seria falso.
    const windowLabel = concelho ? `${concelho.partialBegin.label} – ${concelho.displayEnd.label}, em ${concelho.nome}` : `${national.partialBegin.firstLabel} – ${national.partialEnd.lastLabel}, de norte para sul`;

    let countdownLabel: string;
    let countdownValue: string;
    if (nowMs < beginMs) {
        countdownLabel = concelho ? `Faltam, em ${concelho.nome}` : 'Faltam, para o início em Portugal';
        countdownValue = coarseCountdown(beginMs - nowMs);
    } else if (nowMs <= endMs) {
        countdownLabel = concelho ? `A acontecer em ${concelho.nome}` : 'A acontecer em Portugal';
        countdownValue = 'agora';
    } else {
        countdownLabel = concelho ? `Terminou em ${concelho.nome}` : 'Terminou em Portugal';
        countdownValue = '—';
    }

    const onAdd = () => downloadIcs(concelho ? eventForConcelho(concelho, articleUrl) : eventForCountry(national, articleUrl));

    return html`
        <section class="eclipse-calendar" aria-label="Marcar o eclipse no calendário">
            <div class="eclipse-calendar__action">
                <button type="button" class="eclipse-button" onClick=${onAdd}>
                    <span aria-hidden="true">📅</span>
                    <span>Adicionar ao calendário</span>
                </button>
                <p class="eclipse-calendar__event">${dateLabel} · ${windowLabel}</p>
            </div>

            <div class="eclipse-calendar__countdown">
                <p class="eclipse-calendar__countdown-label">${countdownLabel}</p>
                <p class="eclipse-calendar__countdown-value">${countdownValue}</p>
                <p class="eclipse-calendar__countdown-note">${nowMs < beginMs ? 'para o início — actualizado ao minuto' : 'horas locais calculadas para a sede do concelho'}</p>
            </div>

            <p class="eclipse-calendar__next">${NEXT_ECLIPSES}</p>
        </section>
    `;
}
